/**
 * Kit for connecting to Amazon DynamoDB
 *
 * Encoded credentials (scripts/encode-credentials.js): both accessKeyId and
 * secretAccessKey encrypted with your password—safe to publish in source.
 */

const PBKDF2_ITERATIONS = 310000;

if (typeof window !== "undefined") {
  window.awsCredentialsEncoded = {
    encoded: true,
    credentialsCiphertext:
      "uWzwhV//QeaoDcds1FvdCCWY6EptwC2pyCqlJDYRBXKIqVriyrx5vUgmvjBbintIAzJQYcAmWAN8VBXGawuYrkJdvIyU221g90kyHZkvtJ5pDqk6RrrxN72NHqAad+KM8f2rOky1Mu5ANL78w0TF3JT5bg==",
    salt: "u9XNXhZ8/WLxr8A1PoBYug==",
    iv: "EnftfLKbFvPBhVLi",
  };
}
const IV_LEN = 12;
const AUTH_TAG_LEN = 16;

const userName = (() => {
  try {
    return localStorage.getItem("awsCredentials")
      ? JSON.parse(localStorage.getItem("awsCredentials")).name
      : "Unknown";
  } catch (e) {
    return "Unknown";
  }
})();

const metadataDefaults = {
  author: userName,
  midi: false,
  heat: 5,
  tags: [],
};

class Amakit {
  isAuthenticated = false;
  draftCache = [];

  constructor() {
    this.table = "hydralisk-drafts";
    this.ensureAWS();
  }

  ensureAWS = () => {
    if (!this.AWS && typeof window !== "undefined" && window.AWS) {
      this.AWS = window.AWS;
    }
    if (this.AWS && !this.docClient) {
      this.AWS.config.update({ region: "eu-west-1" });
      this.docClient = new this.AWS.DynamoDB.DocumentClient({
        apiVersion: "2012-08-10",
        region: "eu-west-1",
      });
    }
    return !!(this.AWS && this.docClient);
  };

  loadDrafts = async () => {
    this.ensureAWS();
    try {
      const drafts = await this.getAllDrafts();
      this.draftCache = drafts || [];
      return drafts;
    } catch (err) {
      console.error("Error loading drafts: ", err);
    }
  };

  getCredentials = () => {
    let fromStorage = null;
    try {
      fromStorage = JSON.parse(localStorage.getItem("awsCredentials") || "null");
    } catch (err) {
      console.error("Invalid awsCredentials in localStorage:", err);
    }

    if (fromStorage?.accessKeyId && fromStorage?.secretAccessKey) {
      return fromStorage;
    }

    return null;
  };

  /**
   * Store pre-encoded credentials blob from scripts/encode-credentials.js.
   * Login will prompt for password to decrypt.
   */
  saveEncodedCredentials = async (blob) => {
    if (!blob || !blob.credentialsCiphertext || !blob.salt || !blob.iv) {
      throw new Error("Invalid encoded credentials blob");
    }
    let machineId = localStorage.getItem("machineId");
    if (!machineId) {
      machineId = window.hydraDialog?.prompt
        ? await window.hydraDialog.prompt({
            title: "Machine Setup",
            message: "What is the name of this machine?",
            defaultValue: "laptop",
          })
        : (prompt("What is the name of this machine?") || "unknown");
    }
    machineId = machineId || "unknown";
    localStorage.setItem("machineId", machineId);
    const stored = {
      ...blob,
      machineId,
      name: blob.name || "User",
    };
    localStorage.setItem("awsCredentials", JSON.stringify(stored));
    return stored;
  };

  _decryptSecret = async (ciphertextB64, saltB64, ivB64, password) => {
    const salt = Uint8Array.from(atob(saltB64), (c) => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));
    const ciphertext = Uint8Array.from(atob(ciphertextB64), (c) =>
      c.charCodeAt(0),
    );

    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      enc.encode(password),
      "PBKDF2",
      false,
      ["deriveBits", "deriveKey"],
    );

    const key = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt,
        iterations: PBKDF2_ITERATIONS,
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"],
    );

    const decrypted = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv,
        tagLength: AUTH_TAG_LEN * 8,
      },
      key,
      ciphertext,
    );

    return new TextDecoder().decode(decrypted);
  };

  promptCredentials = async (persist = true) => {
    let machineId = localStorage.getItem("machineId");
    if (!machineId) {
      machineId = window.hydraDialog?.prompt
        ? await window.hydraDialog.prompt({
            title: "Machine Setup",
            message: "What is the name of this machine?",
            defaultValue: "laptop",
          })
        : prompt("What is the name of this machine?");
      machineId = machineId || "unknown";
      localStorage.setItem("machineId", machineId);
    }

    const name = window.hydraDialog?.prompt
      ? await window.hydraDialog.prompt({
          title: "User Profile",
          message: "What is your name?",
          defaultValue: "User",
        })
      : prompt("What is your name?");

    const accessKeyId = window.hydraDialog?.prompt
      ? await window.hydraDialog.prompt({
          title: "AWS Access Key",
          message: "What is your AWS Access Key ID?",
          placeholder: "AKIA...",
        })
      : prompt("What is your AWS Access Key ID?");

    const secretAccessKey = window.hydraDialog?.prompt
      ? await window.hydraDialog.prompt({
          title: "AWS Secret Key",
          message: "What is your AWS Secret Access Key?",
          inputType: "password",
        })
      : prompt("What is your AWS Secret Access Key?");

    if (!accessKeyId || !secretAccessKey) {
      return null;
    }

    const credentials = { name: name || "User", machineId, accessKeyId, secretAccessKey };
    if (persist) {
      localStorage.setItem("awsCredentials", JSON.stringify(credentials));
    }

    return credentials;
  };

  saveCredentials = (name, accessKeyId, secretAccessKey, persist = true) => {
    let machineId = localStorage.getItem("machineId") || "unknown";
    localStorage.setItem("machineId", machineId);

    if (!accessKeyId || !secretAccessKey) {
      return null;
    }

    const credentials = { name: name || "User", machineId, accessKeyId, secretAccessKey };
    if (persist) {
      localStorage.setItem("awsCredentials", JSON.stringify(credentials));
    }

    return credentials;
  };

  login = async (doPrompt = true) => {
    this.ensureAWS();
    if (!this.AWS) {
      return Promise.reject("AWS SDK (window.AWS) is not loaded.");
    }

    let credentials = this.getCredentials();

    // Check for encoded credentials (encrypted blob in storage or window)
    const storedBlob = (() => {
      try {
        const parsed = JSON.parse(localStorage.getItem("awsCredentials") || "null");
        if (parsed?.credentialsCiphertext) return parsed;
      } catch (e) {}
      return (typeof window !== "undefined" && window.awsCredentialsEncoded?.credentialsCiphertext)
        ? window.awsCredentialsEncoded
        : null;
    })();

    if (!credentials && storedBlob) {
      if (!doPrompt) {
        return Promise.reject("No plain credentials found. Decryption password required.");
      }
      const pwd = window.hydraDialog?.prompt
        ? await window.hydraDialog.prompt({
            title: "AWS Credentials",
            message: "Enter password for AWS credentials:",
            inputType: "password",
            confirmText: "Unlock",
          })
        : prompt("Enter password for AWS credentials:");
      if (!pwd) {
        return Promise.reject("Login cancelled: no password provided.");
      }
      try {
        const decryptedStr = await this._decryptSecret(
          storedBlob.credentialsCiphertext,
          storedBlob.salt,
          storedBlob.iv,
          pwd
        );
        const keys = JSON.parse(decryptedStr);
        credentials = {
          name: storedBlob.name || "User",
          machineId: storedBlob.machineId || "unknown",
          accessKeyId: keys.accessKeyId,
          secretAccessKey: keys.secretAccessKey,
        };
      } catch (err) {
        console.error("Decryption error:", err);
        return Promise.reject("Invalid password or decryption failed.");
      }
    }

    if (!credentials) {
      if (!doPrompt) {
        return Promise.reject("No credentials found.");
      }
      credentials = await this.promptCredentials(false);
    }

    if (!credentials || !credentials.accessKeyId || !credentials.secretAccessKey) {
      return Promise.reject("Login cancelled or invalid AWS credentials provided.");
    }

    this.AWS.config.update({
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      region: "eu-west-1"
    });

    if (this.docClient && this.AWS.config.credentials) {
      this.docClient.configure({ credentials: this.AWS.config.credentials });
    }

    return new Promise((resolve, reject) => {
      if (!this.AWS.config.credentials) {
        return reject(new Error("Failed to initialize AWS credentials."));
      }
      this.AWS.config.credentials.get((err) => {
        if (err) {
          console.error("AWS authentication error:", err);
          reject(err);
        } else {
          this.isAuthenticated = true;
          console.log("Logged in as:", credentials.name);
          localStorage.setItem("awsCredentials", JSON.stringify(credentials));
          resolve(credentials);
        }
      });
    });
  };

  getDraft = ({ id, name }) => {
    this.ensureAWS();
    const params = {
      TableName: this.table,
      Key: id ? { id } : { name },
    };
    return new Promise((resolve, reject) => {
      this.docClient.get(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data.Item);
        }
      });
    });
  };

  getAllDrafts = () => {
    this.ensureAWS();
    const params = { TableName: this.table };
    return new Promise((resolve, reject) => {
      this.docClient.scan(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          this.allDrafts = data.Items;
          resolve(data.Items);
        }
      });
    });
  };

  processDraftString = (draftString) => {
    let lines = draftString.split("\n");
    let name = "";
    if (lines[0].trim().startsWith("/*") && lines[0].trim().endsWith("*/")) {
      name = this.getCommentValue(lines[0]);
      lines = lines.slice(1);
    } else {
      name = `Random ${Math.floor(Math.random() * 1000)}`;
    }

    let metadata = {};
    try {
      const metadataLine = lines.find((line) =>
        line.trim().startsWith("/* metadata = "),
      );
      metadata = JSON.parse(
        this.getCommentValue(metadataLine).replace("metadata = ", ""),
      );
    } catch (e) {
      console.error("Cannot parse metadata: ", e);
    }
    metadata = {
      ...metadataDefaults,
      ...metadata,
    };

    return {
      name,
      metadata,
      code: lines.join("\n"),
    };
  };

  addDraft = (draftOrString) => {
    this.ensureAWS();
    let draft = null;

    if (typeof draftOrString === "string") {
      const { name, metadata, code } = this.processDraftString(draftOrString);
      draft = this.shapeDraft({
        name,
        metadata,
        code,
        fullDraft: btoa(draftOrString),
      });
    } else {
      draft = this.shapeDraft(draftOrString);
    }

    const params = {
      TableName: this.table,
      Item: draft,
    };

    return new Promise((resolve, reject) => {
      this.docClient.put(params, (err, response) => {
        if (err) {
          reject(err);
        } else {
          resolve(draft);
        }
      });
    });
  };

  uploadDraftObj = (draft) => {
    this.ensureAWS();
    const params = {
      TableName: this.table,
      Item: draft,
    };
    return new Promise((resolve, reject) => {
      this.docClient.put(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  };

  // Drop by id or name
  dropDraft = ({ id, name }) => {
    this.ensureAWS();
    const params = {
      TableName: this.table,
      Key: id ? { id } : { name },
    };
    return new Promise((resolve, reject) => {
      this.docClient.delete(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  };

  generateId = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = Math.floor(Math.random() * 16);
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  getCommentValue = (comment) => {
    const start = comment.indexOf("/*") + 2;
    const end = comment.indexOf("*/");
    return comment.substring(start, end).trim();
  };

  shapeDraft = (draft) => {
    const id = draft.id || this.generateId();
    const name = draft.name || `Random ${Math.floor(Math.random() * 1000)}`;
    const metadata = {
      ...metadataDefaults,
      ...(draft.metadata || {}),
    };
    const code = draft.code.replace(/^\/\* metadata.*$/m, "").trim();
    const fullDraft = btoa(
      `/* ${name} */\n${code}\n/* metadata = ${JSON.stringify(metadata)}*/`,
    );

    return {
      id,
      name,
      metadata,
      fullDraft,
      code,
    };
  };
}

window.amakit = new Amakit();

if (typeof window !== "undefined" && window.HydraliskPlugins) {
  window.HydraliskPlugins.register({
    id: "amakit",
    name: "Amazon DynamoDB Draft Storage",
    init(app) {
      app.expose("amakit", window.amakit);
    },
  });
}
